import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, HelpCircle, Share, ChevronDown, ChevronUp,
  PanelLeftClose, PanelLeftOpen, LogOut, Settings, CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { getUserContent } from '../../utils/firebaseService';
import { fetchSubscription } from '../../utils/fetchSubscription';
import SharePopup from '../popup/SharePopup';
import FeedbackPopup from '../popup/Feedback';
import { db } from '../../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

// Helper function to truncate long titles
const truncateTitle = (title, maxLength = 25) => {
  if (!title) return '';
  return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
};

const MenuItem = ({ icon, text, hasSubmenu, onClick }) => (
  <div className="flex items-center px-4 py-2 my-1 rounded-md cursor-pointer hover:bg-gray-100 transition-colors" onClick={onClick}>
    <div className="flex-shrink-0">{icon}</div>
    <span className="ml-3 text-sm">{text}</span>
    {hasSubmenu && <ChevronDown className="ml-auto mx-2" size={16} />}
  </div>
);

export default function SideNavDesktop() {
  const { collapsed, setCollapsed, isMobile } = useSidebar();
  const { user, logout } = useAuth();
  const [recentContent, setRecentContent] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userPlan, setUserPlan] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false); // <--- added state
  const navigate = useNavigate();
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchRecentContent = async () => {
      if (user && !collapsed) {
        setIsLoading(true);
        try {
          const content = await getUserContent(); 
          setRecentContent(content);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchRecentContent();
  }, [user, collapsed]);

  // Fetch subscription plan using fetchSubscription
  useEffect(() => {
    const getPlan = async () => {
      if (user?.uid) {
        try {
          const { subscription } = await fetchSubscription(user.uid);
          setUserPlan(subscription?.plan || '');
        } catch (err) {
          setUserPlan('');
        }
      } else {
        setUserPlan('');
      }
    };
    getPlan();
  }, [user]);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNewChat = () => {
    navigate('/');
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
    }
  };

  const handleSettings = () => {
    navigate('/settings');
    setShowProfileMenu(false);
  };

  const handlePlan = () => {
    navigate('/plans');
    setShowProfileMenu(false);
  };

  if (!isDesktop || isMobile) return null;

  return (
    <>
      {!collapsed && (
        <nav className="fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 shadow-lg z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Link to="/">
              <img src="/1xSkills-Logo.webp" alt="1xSkills" className="h-12 w-auto mx-2 object-contain" />
            </Link>
            <button
              onClick={() => setCollapsed(true)}
              className="flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
            >
              <PanelLeftClose size={22} className="mx-2" />
            </button>
          </div>

          {/* Main Scrollable Area */}
          <div className="flex-1 overflow-y-auto">
            {user ? (
              <>
                {/* New Chat Button */}
                <div className="p-4">
                  <button 
                    onClick={handleNewChat}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md flex items-center justify-center shadow-sm px-4"
                  >
                    <Plus size={18} className="mr-2" />
                    <span>New Chat</span>
                  </button>
                </div>

                {/* RECENTS */}
                <div className="p-2">
                  <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">RECENTS</h3>
                  {isLoading ? (
                    <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
                  ) : recentContent.length > 0 ? (
                    recentContent.map((content) => (
                      <Link key={content.id} to={`/c/${content.id}`}>
                        <div className="flex items-center px-4 py-2 my-1 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                          <span className="text-sm">
                            {truncateTitle(content.title || `${content.id}`)}
                          </span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No recent content</div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Welcome to 1xSkills</h3>
                <p className="text-sm text-gray-600 mb-2">Your personalized AI Knowledge Workspace</p>
                
                <p className="text-sm text-gray-600 mb-6">
                  Sign in to get started. <br></br><br></br>
                  We're constantly improving the platform, and if you have any feedback, 
                  we'd love to hear from you.
                </p>
              </div>
            )}
          </div>

          {/* Bottom - Non-scrollable */}
          <div className="border-t border-gray-200 p-2">
            {user ? (
              <>
                {/* HELP & TOOLS */}
                <div className="mb-2">
                  <MenuItem icon={<HelpCircle size={18} className="text-gray-600 mx-2" />} text="Feedback" onClick={() => setShowFeedbackPopup(true)} />
                  <MenuItem icon={<Share size={18} className="text-gray-600 mx-2" />} text="Refer" onClick={() => setShowSharePopup(true)} />
                </div>

                {/* User Profile with Dropdown */}
                <div className="relative" ref={profileMenuRef}>
                  {/* Profile Dropdown Menu - Positioned ABOVE the profile section */}
                  {showProfileMenu && (
                    <div className="absolute -top-32 left-0 right-0 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden z-50">
                      <div 
                        className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer"
                        onClick={handleSettings}
                      >
                        <Settings size={16} className="text-gray-600 mr-2" />
                        <span className="text-sm">Settings</span>
                      </div>
                      <div 
                        className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer"
                        onClick={handlePlan}
                      >
                        <CreditCard size={16} className="text-gray-600 mr-2" />
                        <span className="text-sm">Plan</span>
                      </div>
                      <div 
                        className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer border-t border-gray-100"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} className="text-gray-600 mr-2" />
                        <span className="text-sm">Log out</span>
                      </div>
                    </div>
                  )}
                
                  {/* User Profile - Clickable to toggle dropdown */}
                  <div 
                    className="flex items-center p-2 rounded-md bg-gray-50 hover:bg-gray-100 cursor-pointer"
                    onClick={handleProfileClick}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 mx-2 flex items-center justify-center overflow-hidden">
                      {user?.profilePhoto ? (
                        <img src={user.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-semibold text-blue-600">
                          {user?.displayName?.[0] || user?.email?.[0] || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="ml-3 truncate flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{truncateTitle(user?.displayName || 'User', 20)}</p>
                      <p className="text-xs text-gray-500 pt-2 truncate">{truncateTitle(user?.email || 'No email available', 25)}</p>
                      {/* Show plan below email */}
                      {userPlan && (
                        <p className="text-xs text-blue-800 font-bold pt-1">{userPlan} Plan</p>
                      )}
                    </div>
                    {showProfileMenu ? 
                      <ChevronUp size={16} className="text-gray-500 ml-auto" /> : 
                      <ChevronDown size={16} className="text-gray-500 ml-auto" />
                    }
                  </div>
                </div>
              </>
            ) : (
              <Link 
                to="/login" 
                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md"
              >
                Sign in
              </Link>
            )}
          </div>
        </nav>
      )}

      {/* Collapsed Mode */}
      {collapsed && (
        <div className="fixed top-0 bg-white left-0 w-full p-4 flex justify-between items-start z-50">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img src="/1xSkills-Logo.webp" alt="1xSkills" className="h-12 w-auto mx-2 object-contain" />
            </Link>
            <button
              onClick={() => setCollapsed(false)}
              className="flex h-10 w-10 mx-4 items-center justify-center rounded-md text-gray-500 bg-gray-100"
            >
              <PanelLeftOpen size={20} className="mx-2" />
            </button>
          </div>
        </div>
      )}
      
      {/* Upgrade Plan Button - Always visible */}
      <div className="fixed right-5 top-3 z-50">
        <Link
          to="/plans"
          className="inline-flex items-center justify-center h-10 px-4 text-center leading-loose text-sm text-white font-semibold border border-blue-700 bg-gradient-to-b from-blue-800 to-blue-600 rounded-lg transition duration-200 hover:bg-blue-600 shadow-lg"
        >
          Upgrade Plan
        </Link>
      </div>

      {/* Share Popup - Rendered outside nav for correct overlay */}
      <SharePopup isOpen={showSharePopup} onClose={() => setShowSharePopup(false)} link={typeof window !== 'undefined' ? window.location.origin : ''} />
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        initialEmail={user?.email || ''}
        onSubmit={async ({ message, email }) => {
          if (user?.uid) {
            try {
              await addDoc(collection(db, 'users', user.uid, 'Feedback'), {
                message,
                timestamp: serverTimestamp()
              });
            } catch (error) {
              console.error('Error saving feedback:', error);
              throw error;
            }
          }
        }}
      />
    </>
  );
}